// Single source of truth for role-hierarchy and role-check logic (Req 7).
//
// Replaces the pre-consolidation, triplicated implementations previously
// maintained independently in:
//   - client/src/lib/rbac.ts            (ROLE_HIERARCHY, hasRole, getUserPermissions)
//   - client/src/lib/authUtils.ts       (hasRole, hasAnyRole, getUserRoles)
//   - client/src/lib/unifiedReview/gating.ts   (VCIO_PLUS_ROLES, isVcioPlus)
//   - client/src/lib/reviewWorkflow/gating.ts  (VCIO_PLUS_ROLES, isVcioPlus)
//
// This module is the only place role ordering and role-check behavior are
// defined; the modules above are migrated to re-export/delegate to it.
//
// Every exported function here is total over `any` input: a missing, null,
// or unrecognized role never throws and never satisfies a check (Req 7.8).

/** The set of roles recognized by the system, ordered by seniority. */
export type Role = "owner" | "admin" | "vcio" | "alignment_engineer";

/**
 * Numeric ordering for "at least this seniority" checks (used by
 * `hasMinimumRole` and, transitively, `getUserPermissions`). Higher values
 * indicate more seniority/permissions.
 */
export const ROLE_HIERARCHY: Record<Role, number> = {
  owner: 4,
  admin: 3,
  vcio: 2,
  alignment_engineer: 1,
};

/**
 * Narrows a user's `role` field to a known {@link Role}, or `null` if the
 * value is missing, not a string, or not one of the roles defined by
 * {@link ROLE_HIERARCHY} (Req 7.8).
 */
function normalizeRole(user: any): Role | null {
  const role = user?.role;
  if (typeof role !== "string") return null;
  return role in ROLE_HIERARCHY ? (role as Role) : null;
}

/**
 * Exact-role-membership check, accepting either a single role or an array of
 * roles (Req 7.2) — replacing both the single-role-only `hasRole` from
 * `authUtils.ts` and the array-accepting `hasRole` from `rbac.ts` with one
 * shared signature and one shared behavior.
 *
 * Carries the single "owner satisfies admin" exception from the
 * pre-consolidation `authUtils.hasAnyRole` (Req 7.3): a user whose role is
 * `"owner"` satisfies a check that includes `"admin"`, even though `"owner"`
 * itself was not requested.
 *
 * Returns `false` for any user whose role is missing, `null`, or not a
 * recognized {@link Role} (Req 7.8), regardless of the roles requested.
 */
export function hasRole(
  user: any,
  roleOrRoles: Role | Role[] | string | string[],
): boolean {
  const userRole = normalizeRole(user);
  if (userRole === null) return false;

  const rolesToCheck = Array.isArray(roleOrRoles) ? roleOrRoles : [roleOrRoles];

  if (rolesToCheck.includes(userRole)) return true;

  // "owner" satisfies an "admin" check.
  if (userRole === "owner" && rolesToCheck.includes("admin")) return true;

  return false;
}

/**
 * Alias of {@link hasRole}. Both pre-consolidation implementations
 * (`rbac.ts`'s `hasAnyRole` and `authUtils.ts`'s `hasAnyRole`) already treated
 * "has any of these roles" as equivalent to `hasRole` with an array argument.
 */
export function hasAnyRole(user: any, roles: (Role | string)[]): boolean {
  return hasRole(user, roles);
}

/**
 * Numeric-rank comparison: `true` iff the user's role is recognized AND its
 * rank in {@link ROLE_HIERARCHY} is at least the rank of `minRole`. This is a
 * broader relation than {@link hasRole}'s exact-match(+owner-exception) rule
 * — e.g. `hasMinimumRole(user, "vcio")` is satisfied by `vcio`, `admin`, and
 * `owner` alike.
 *
 * Returns `false` for any user whose role is missing, `null`, or not a
 * recognized {@link Role} (Req 7.8).
 */
export function hasMinimumRole(user: any, minRole: Role): boolean {
  const userRole = normalizeRole(user);
  if (userRole === null) return false;
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[minRole];
}

/** The vCIO+ role set, defined once (Req 7.6, 7.7, 7.10). */
export const VCIO_PLUS_ROLES: Role[] = ["vcio", "admin", "owner"];

/** True iff the user holds a {@link VCIO_PLUS_ROLES} role. */
export function isVcioPlus(user: any): boolean {
  return hasAnyRole(user, VCIO_PLUS_ROLES);
}

/**
 * Returns the user's role(s) as a string array, matching the pre-consolidation
 * `authUtils.getUserRoles` behavior: the user's raw `role` value wrapped in a
 * single-element array when truthy, or an empty array otherwise.
 */
export function getUserRoles(user: any): string[] {
  return user?.role ? [user.role] : [];
}

/**
 * The permission set derived from a user's role, unchanged in shape from the
 * pre-consolidation `client/src/lib/rbac.ts`.
 */
export interface RolePermissions {
  // User Management
  canViewUsers: boolean;
  canCreateUsers: boolean;
  canEditUsers: boolean;
  canDeleteUsers: boolean;
  canManageRoles: boolean;

  // Customer Management
  canViewCustomers: boolean;
  canCreateCustomers: boolean;
  canEditCustomers: boolean;
  canDeleteCustomers: boolean;

  // Assessment Management
  canViewAssessments: boolean;
  canCreateAssessments: boolean;
  canEditAssessments: boolean;
  canDeleteAssessments: boolean;
  canApproveAssessments: boolean;
  canReassignAssessments: boolean;

  // Template Management
  canViewTemplates: boolean;
  canCreateTemplates: boolean;
  canEditTemplates: boolean;
  canDeleteTemplates: boolean;

  // QBR Management
  canViewQbrs: boolean;
  canCreateQbrs: boolean;
  canEditQbrs: boolean;
  canDeleteQbrs: boolean;

  // SOP Management
  canViewSops: boolean;
  canCreateSops: boolean;
  canEditSops: boolean;
  canDeleteSops: boolean;

  // Integration Management
  canViewIntegrations: boolean;
  canManageIntegrations: boolean;

  // Backup Management
  canViewBackups: boolean;
  canManageBackups: boolean;

  // Notification Management
  canViewNotifications: boolean;
  canManageNotifications: boolean;

  // System Administration
  canAccessSettings: boolean;
  canManageSystem: boolean;
  canViewAuditLogs: boolean;

  // Ownership Management
  canTransferOwnership: boolean;
}

/**
 * Reimplemented purely on {@link hasRole}/{@link hasMinimumRole} (moved from
 * `rbac.ts`). Produces the same outcome as the pre-consolidation
 * `rbac.getUserPermissions` for every defined role value (Req 7.9):
 * `hasRole(user, "admin")` already covers `{admin, owner}` via the
 * owner-satisfies-admin exception, matching the old
 * `hasRole('admin') || hasRole('owner')` pattern used throughout.
 *
 * For a missing/unrecognized role, every permission gated by `hasRole`/
 * `hasMinimumRole` resolves to `false`; only the permissions that were always
 * `true` for any authenticated user remain `true`, consistent with Req 7.8's
 * safety requirement for role-check functions this module composes.
 */
export function getUserPermissions(user: any): RolePermissions {
  return {
    // User Management - Admin and Owner
    canViewUsers: hasRole(user, "admin"),
    canCreateUsers: hasRole(user, "admin"),
    canEditUsers: hasRole(user, "admin"),
    canDeleteUsers: hasRole(user, "admin"),
    canManageRoles: hasRole(user, "admin"),

    // Customer Management
    canViewCustomers: true, // All authenticated users
    canCreateCustomers: hasMinimumRole(user, "vcio"),
    canEditCustomers: hasMinimumRole(user, "vcio"),
    canDeleteCustomers: hasRole(user, "admin"),

    // Assessment Management
    canViewAssessments: true, // All authenticated users (filtered by role in API)
    canCreateAssessments: true, // All authenticated users
    canEditAssessments: true, // All authenticated users (own assessments)
    canDeleteAssessments: hasMinimumRole(user, "vcio"),
    canApproveAssessments: hasMinimumRole(user, "vcio"),
    canReassignAssessments: hasMinimumRole(user, "vcio"),

    // Template Management
    canViewTemplates: true, // All authenticated users
    canCreateTemplates: hasMinimumRole(user, "vcio"),
    canEditTemplates: hasMinimumRole(user, "vcio"),
    canDeleteTemplates: hasRole(user, "admin"),

    // QBR Management - vCIO and Admin only
    canViewQbrs: hasMinimumRole(user, "vcio"),
    canCreateQbrs: hasMinimumRole(user, "vcio"),
    canEditQbrs: hasMinimumRole(user, "vcio"),
    canDeleteQbrs: hasRole(user, "admin"),

    // SOP Management
    canViewSops: true, // All authenticated users
    canCreateSops: hasMinimumRole(user, "vcio"), // Only vCIO and Admin can create
    canEditSops: hasMinimumRole(user, "vcio"), // Only vCIO and Admin can edit
    canDeleteSops: hasMinimumRole(user, "vcio"), // Only vCIO and Admin can delete

    // Integration Management - Admin and Owner
    canViewIntegrations: hasRole(user, "admin"),
    canManageIntegrations: hasRole(user, "admin"),

    // Backup Management - Admin and Owner
    canViewBackups: hasRole(user, "admin"),
    canManageBackups: hasRole(user, "admin"),

    // Notification Management
    canViewNotifications: true, // All authenticated users
    canManageNotifications: hasRole(user, "admin"),

    // System Administration
    canAccessSettings: true, // All authenticated users (own settings)
    canManageSystem: hasRole(user, "admin"),
    canViewAuditLogs: hasRole(user, "admin"),

    // Ownership Management
    canTransferOwnership: hasRole(user, "owner"),
  };
}
